import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";

import Select, {
  Options,
  MultiValue,
  ActionMeta,
  SingleValue,
} from "react-select";
import CreatableSelect from "react-select/creatable";
import { DateObject } from "react-multi-date-picker";

import useAxios from "../../services/useAxios";

import CustomButton from "../kits/button/CustomButton";
import { CustomInput } from "../kits/Inputs/Inputs";
import DatePiker from "../datepiker/datePiker";

import CampaignContext from "./CampaignContext";

import type {
  FindProductServicesResponseType,
  FindTagResponseType,
  InsertResponseType,
  InsertTagRequestType,
  OptionType,
} from "./SiCaTypes";
import { StatusOptions } from "./SiCaData";

const DetailTab: React.FC = () => {
  const { execute: findProductServicesAPI } = useAxios(
    "api/Find_ProductServices",
    { conditions: "main_type/=/1" }
  );
  const { execute: findTagsAPI } = useAxios("api/Find_tag");
  const { execute: InsertTag } = useAxios("api/Insert_tag");

  const { mainDetails, moreDetails, changeMainDetails, submitDetails } =
    useContext(CampaignContext);

  const { campaignId } = useParams<{ campaignId: string }>();

  // Temp Input Data
  const [starttm, setStarttm] = useState<DateObject>();
  const [endtm, setEndtm] = useState<DateObject>();

  // Dynamic Data
  const [productOptions, setProductOptions] = useState<Options<OptionType>>([]);
  const [tagOptions, setTagOptions] = useState<Options<OptionType>>([]);

  // Statistics Data
  const statisticData = useMemo(
    () => [
      {
        name: "تعداد مراحل",
        value: moreDetails.stepsCount,
      },
      {
        name: "تعداد مراحل انجام‌ شده",
        value: moreDetails.finishedStepsCount,
      },
      {
        name: "تعداد سرنخ‌",
        value: moreDetails.clueCount,
      },
      {
        name: "تعداد فرصت‌",
        value: moreDetails.chanceCount,
      },
      {
        name: "تعداد فروش",
        value: moreDetails.sellCount,
      },
      {
        name: "مجموع فروش",
        value: `${moreDetails.sellAmount} تومان`,
      },
    ],
    [moreDetails]
  );

  const getTags = useCallback(() => {
    findTagsAPI().then((res: FindTagResponseType) => {
      setTagOptions(
        res.map((productService) => ({
          label: productService.text,
          value: "" + productService.id,
        }))
      );
    });
  }, []);

  const titleInputHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      changeMainDetails({ ...mainDetails, title: event.target.value });
    },
    [changeMainDetails, mainDetails]
  );

  const descriptionInputHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      changeMainDetails({ ...mainDetails, description: event.target.value });
    },
    [changeMainDetails, mainDetails]
  );

  const starttmInputHandler = useCallback(
    (event: DateObject | DateObject[] | null) => {
      if (event instanceof DateObject) {
        setStarttm(event);
        changeMainDetails({ ...mainDetails, starttm: "" + event.unix });
      }
    },
    [changeMainDetails, mainDetails]
  );
  const endtmInputHandler = useCallback(
    (event: DateObject | DateObject[] | null) => {
      if (event instanceof DateObject) {
        setEndtm(event);
        changeMainDetails({ ...mainDetails, endtm: "" + event.unix });
      }
    },
    [changeMainDetails, mainDetails]
  );

  const productSelectHandler = useCallback(
    (newValue: MultiValue<OptionType>, action: ActionMeta<OptionType>) => {
      changeMainDetails({
        ...mainDetails,
        products: newValue.map((nv) => nv.value),
      });
    },
    [changeMainDetails, mainDetails]
  );

  const tagSelectHandler = useCallback(
    (newValue: MultiValue<OptionType>, action: ActionMeta<OptionType>) => {
      if (action.action === "create-option") {
        // @ts-ignore
        const theNewOne = newValue.find((val) => val.__isNew__);
        if (theNewOne) {
          const data: InsertTagRequestType = {
            text: theNewOne.label,
            mtm: "" + new DateObject().unix,
            status: "1",
          };
          InsertTag(data).then((res: InsertResponseType) => {
            if (Boolean(res.status)) {
              getTags();
              changeMainDetails({
                ...mainDetails,
                tags: newValue.map((nv) => {
                  if (nv.value === theNewOne.value) {
                    return res.result.id;
                  }
                  return nv.value;
                }),
              });
            }
          });
        }
      } else {
        changeMainDetails({
          ...mainDetails,
          tags: newValue.map((nv) => nv.value),
        });
      }
    },
    [changeMainDetails, mainDetails]
  );

  const statusSelectHandler = useCallback(
    (newValue: SingleValue<OptionType>, action: ActionMeta<OptionType>) => {
      changeMainDetails({
        ...mainDetails,
        status: newValue?.value || "",
      });
    },
    [changeMainDetails, mainDetails]
  );

  useEffect(() => {
    findProductServicesAPI().then((res: FindProductServicesResponseType) => {
      setProductOptions(
        res.map((productService) => ({
          label: productService.title,
          value: "" + productService.id,
        }))
      );
    });
    getTags();
  }, []);

  useEffect(() => {
    if (!isNaN(+mainDetails.starttm) && +mainDetails.starttm !== 0) {
      setStarttm(
        new DateObject({
          date: new Date(+mainDetails.starttm * 1000),
        })
      );
    }
    if (!isNaN(+mainDetails.endtm) && +mainDetails.endtm !== 0) {
      setEndtm(
        new DateObject({
          date: new Date(+mainDetails.endtm * 1000),
        })
      );
    }
  }, [mainDetails]);

  return (
    <>
      <form className="container-lg">
        <h5 className="mb-3" style={{ fontWeight: "bold" }}>
          اطلاعات اصلی
        </h5>
        <div className="row row-cols-1 row-cols-md-2 g-4 mb-4">
          <div className="col">
            <label
              className="form-label"
              style={{
                color: "#677788",
                fontSize: "0.75rem",
              }}
            >
              زمان آغاز کمپین
            </label>
            <div>
              <DatePiker
                type_calender=""
                location="center"
                color=""
                bg=""
                buttons
                time
                just_time={false}
                onlyYear={false}
                onlyMonth={false}
                hideWeekDays={false}
                hideMonth={false}
                highlightDay={false}
                weekend=""
                color_highlight=""
                disableMonthPicker={false}
                disableDay={false}
                disableYearPicker={false}
                plugins={false}
                mood="single"
                inputSize="md"
                value={starttm}
                setValue={starttmInputHandler}
              />
            </div>
          </div>
          <div className="col">
            <label
              className="form-label"
              style={{
                color: "#677788",
                fontSize: "0.75rem",
              }}
            >
              زمان پایان کمپین
            </label>
            <div>
              <DatePiker
                type_calender=""
                location="center"
                color=""
                bg=""
                buttons
                time
                just_time={false}
                onlyYear={false}
                onlyMonth={false}
                hideWeekDays={false}
                hideMonth={false}
                highlightDay={false}
                weekend=""
                color_highlight=""
                disableMonthPicker={false}
                disableDay={false}
                disableYearPicker={false}
                plugins={false}
                mood="single"
                inputSize="md"
                value={endtm}
                setValue={endtmInputHandler}
              />
            </div>
          </div>
          <CustomInput
            inputContainer="col"
            label="عنوان"
            placeholder="تخفیفات  زمستانی"
            inputValue={mainDetails.title}
            type="text"
            // @ts-ignore
            onChangeFunc={titleInputHandler}
          />
          <CustomInput
            inputContainer="col"
            label="توضیحات"
            inputValue={mainDetails.description}
            type="textarea"
            // @ts-ignore
            onChangeFunc={descriptionInputHandler}
          />
          <div className="col">
            <label
              className="form-label"
              style={{
                color: "#677788",
                fontSize: "0.75rem",
              }}
            >
              محصولات
            </label>
            <Select
              value={productOptions.filter((po) =>
                mainDetails.products.find((p) => +p === +po.value)
              )}
              options={productOptions}
              isMulti
              onChange={productSelectHandler}
            />
          </div>
          <div className="col">
            <label
              className="form-label"
              style={{
                color: "#677788",
                fontSize: "0.75rem",
              }}
            >
              برچسب‌ها
            </label>
            <CreatableSelect
              value={tagOptions.filter((tag) =>
                mainDetails.tags.find((t) => +t === +tag.value)
              )}
              options={tagOptions}
              isMulti
              onChange={tagSelectHandler}
            />
          </div>
          <div className="col">
            <label
              className="form-label"
              style={{
                color: "#677788",
                fontSize: "0.75rem",
              }}
            >
              وضعیت
            </label>
            <Select
              value={StatusOptions.find((s) => s.value === mainDetails.status)}
              options={StatusOptions}
              onChange={statusSelectHandler}
            />
          </div>
        </div>
        <hr className="w-75 mx-auto" />
        <h5 className="mb-4" style={{ fontWeight: "bold" }}>
          آمار کلی
        </h5>
        <div className="row g-4 row-cols-2 row-cols-md-3">
          {statisticData.map((data, index) => (
            <div
              className="col d-flex justify-content-between"
              key={`campaign-statistic-data-${index}`}
              style={{ fontSize: "0.8rem" }}
            >
              <p>{data.name}:</p> <p className="ms-2">{data.value}</p>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-center mt-5 mb-3">
          <div style={{ width: 200 }}>
            <CustomButton
              className="w-100 justify-content-center"
              color="success"
              btnType="submit"
              onClick={submitDetails}
            >
              ثبت {campaignId && "تغییرات"}
            </CustomButton>
          </div>
        </div>
      </form>
    </>
  );
};

export default DetailTab;
